import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PageBreadcrumbComponent } from '../../../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { BadgeComponent } from '../../../../../shared/components/ui/badge/badge.component';
import { AuthService } from '../../../../auth-pages/services/auth.service';
import { MeetingCancellationReasonApiService } from '../../../services/meeting-cancellation-reason-api.service';
import { MeetingCancellationReasonDrawerService } from '../../../services/meeting-cancellation-reason-drawer.service';
import { MeetingCancellationReasonResponse } from '../../../models/meeting.model';
import { ConfirmDialogService } from '../../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../../../shared/directives/has-permission.directive';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../../../shared/utils/pagination.constants';

@Component({
  selector: 'app-cancellation-reasons-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    BadgeComponent,
    HasPermissionDirective,
  ],
  templateUrl: './cancellation-reasons-list.component.html',
})
export class CancellationReasonsListComponent implements OnInit, OnDestroy {
  items: MeetingCancellationReasonResponse[] = [];
  loading = false;
  error = '';

  // Filtros
  filtroSearch = '';
  filtroActive: '' | 'true' | 'false' = '';

  // Paginación
  globalPage = 0;
  globalTotalPages = 0;
  globalTotalElements = 0;
  pageSize = 10;
  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private api: MeetingCancellationReasonApiService,
    private drawerService: MeetingCancellationReasonDrawerService,
    private authService: AuthService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    const savedPageSize = localStorage.getItem('cancellation-reasons:pageSize');
    if (savedPageSize) this.pageSize = +savedPageSize;
    this.cargar();
    this.drawerService.saved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.cargar());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get businessId(): number {
    return this.authService.getCurrentUser()?.businessId || 1;
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    this.api.list(this.businessId, this.globalPage, this.pageSize).subscribe({
      next: (page) => {
        let data = [...page.content];
        if (this.filtroSearch.trim()) {
          const term = this.filtroSearch.trim().toLowerCase();
          data = data.filter((i) => i.name.toLowerCase().includes(term));
        }
        if (this.filtroActive !== '') {
          const want = this.filtroActive === 'true';
          data = data.filter((i) => !!i.isActive === want);
        }
        this.items = data.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        this.globalTotalPages = page.totalPages;
        this.globalTotalElements = page.totalElements;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error cargando motivos';
        this.loading = false;
      },
    });
  }

  buscar(): void {
    this.globalPage = 0;
    this.cargar();
  }

  onPageSizeChange(): void {
    localStorage.setItem('cancellation-reasons:pageSize', this.pageSize.toString());
    this.globalPage = 0;
    this.cargar();
  }

  goToPage(p: number): void {
    if (p < 0 || p >= this.globalTotalPages) return;
    this.globalPage = p;
    this.cargar();
  }

  nuevo(): void {
    this.drawerService.open();
  }

  editar(item: MeetingCancellationReasonResponse): void {
    this.drawerService.open({ reasonId: item.id });
  }

  toggle(item: MeetingCancellationReasonResponse): void {
    this.api
      .update(item.id, this.businessId, {
        businessId: this.businessId,
        name: item.name,
        sortOrder: item.sortOrder,
        isActive: !item.isActive,
      })
      .subscribe({ next: () => this.cargar() });
  }

  async eliminar(item: MeetingCancellationReasonResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar motivo',
      message: `¿Eliminar "${item.name}"?`
    });
    if (!ok) return;
    this.api.delete(item.id, this.businessId).subscribe({
      next: () => { this.cargar(); this.toast.deleted('Razón de cancelación', `Se eliminó "${item.name}" correctamente.`); },
      error: (err) => { this.toast.error({ title: 'Error', message: err.error?.message || 'Error al eliminar' }); },
    });
  }

  trackById = (_: number, item: MeetingCancellationReasonResponse) => item.id;
}
